import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react'
import { Box, Skeleton, Text, useElementSize } from '@salestrak/ui'
import { GoldenHourLineChart } from './charts/GoldenHourLineChart'
import { Section } from './Section'
import { InfoTooltip } from './InfoTooltip'
import type { DashboardLabels, GoldenHourBucket, GoldenHourRange } from '#app/lib/data'
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";
// Ghost chart data — bell curve peaking ~3pm, used when no activity recorded yet
const GHOST_KNOCK_VALUES = [0, 0, 0, 0, 0, 5, 10, 15, 25, 30, 28, 26, 30, 35, 55, 80, 90, 82, 65, 42, 22, 12, 5, 2]
const GHOST_CONTACTS_VALUES = [0, 0, 0, 0, 0, 3, 6, 10, 16, 20, 18, 16, 20, 24, 38, 55, 65, 58, 45, 28, 14, 8, 3, 1]

type GoldenHourChartProps = Omittable<{
  /** Pre-computed hourly data from backend analytics */
  analyticsHourly?: GoldenHourBucket[] | null
  /** Pre-computed all-time hourly data from backend analytics */
  analyticsAllTimeHourly?: GoldenHourBucket[] | null
  /** Pre-computed golden range from backend analytics */
  analyticsGoldenRange?: GoldenHourRange | null
  labels?: DashboardLabels | null
  onHourPress?: (hour: number, label: string) => void
  /** Compact mode — just legend + chart, no Section wrapper or stats */
  compact?: boolean
}>;

function formatHour(h: number) {
  if (h === 0) return '12a'
  if (h < 12) return `${h}a`
  if (h === 12) return '12p'
  return `${h - 12}p`
}

function formatHourRange(start: number, end: number) {
  if (start === end) return formatHour(start)
  return `${formatHour(start)}\u2013${formatHour(end)}`
}

export function GoldenHourChart({ analyticsHourly, analyticsAllTimeHourly, analyticsGoldenRange, labels, onHourPress, compact }: GoldenHourChartProps) {
  // Box reports no layout of its own; `useElementSize` measures it with a
  // ResizeObserver.
  const { ref: chartBoxRef, size: chartBoxSize } = useElementSize<HTMLDivElement>()
  const chartWidth = chartBoxSize.width
  const [hoveredHour, setHoveredHour] = useState<number | null>(null)
  const chartWrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!chartWrapRef.current) return
    const node = chartWrapRef.current as unknown as HTMLElement
    const svgs = node.querySelectorAll('svg')
    svgs.forEach((svg) => { svg.style.overflow = 'visible' })
  })

  const goldenRange = useMemo(() => {
    return analyticsGoldenRange ? { start: analyticsGoldenRange.start, end: analyticsGoldenRange.end } : null
  }, [analyticsGoldenRange])

  const { allCounts, contactCounts, hasContacts, goldenStats } = useMemo(() => {
    const allCounts = new Array<number>(24).fill(0)
    const contactCounts = new Array<number>(24).fill(0)
    let hasContacts = false

    if (analyticsHourly) {
      for (const b of analyticsHourly) {
        allCounts[b.hour] = b.kncCount
        contactCounts[b.hour] = b.contactCount
        if (b.contactCount > 0) hasContacts = true
      }
    }

    const goldenStats = analyticsGoldenRange ? {
      goldenKnocks: analyticsGoldenRange.goldenKnocks,
      goldenContacts: analyticsGoldenRange.goldenContacts,
      goldenRate: analyticsGoldenRange.goldenRate,
      otherKnocks: analyticsGoldenRange.otherKnocks,
      otherContacts: analyticsGoldenRange.otherContacts,
      otherRate: analyticsGoldenRange.otherRate,
    } : null

    return { allCounts, contactCounts, hasContacts, goldenStats }
  }, [analyticsHourly, analyticsGoldenRange])

  // Theme-derived chart style values
  const mutedColor = "var(--muted-foreground)"
  const borderColor = "var(--border)"
  const ghostLabelStyle = { color: mutedColor, fontSize: 9, textAlign: 'center' as const }
  const ghostYAxisStyle = { color: mutedColor, fontSize: compact ? 8 : 10 }

  const subtitle = goldenRange
    ? `Peak contact window: ${formatHourRange(goldenRange.start, goldenRange.end)}`
    : undefined

  const isLoading = !analyticsHourly
  const hasData = analyticsHourly?.some(b => b.kncCount > 0 || b.contactCount > 0) ?? false

  const yAxisWidth = 40
  const chartHeight = compact ? 100 : 180
  const initialSpacing = 12
  const endSpacing = 12

  const legend = (
    <Box direction="row" gap="md">
      <Box direction="row" gap="xs" align="center">
        <Text size="xs" color="light-purple">{'\u25CF'}</Text>
        <Text size="xs" variant="muted">{labels?.contactVerb ? `${labels.contactVerb.charAt(0).toUpperCase()}${labels.contactVerb.slice(1)} No Contact` : 'No Contact'}</Text>
      </Box>
      {hasContacts && (
        <Box direction="row" gap="xs" align="center">
          <Text size="xs" color="teal">{'\u25CF'}</Text>
          <Text size="xs" variant="muted">Contacts</Text>
        </Box>
      )}
      {goldenRange && (
        <Box direction="row" gap="xs" align="center">
          <Text size="xs" color="yellow">{'\u25CF'}</Text>
          <Text size="xs" variant="muted">Golden Hour</Text>
        </Box>
      )}
    </Box>
  )

  const chartBox = (
    <Box ref={chartBoxRef} paddingTop={compact ? 'xs' : 'sm'}>
      {isLoading ? (
        <Skeleton className={compact ? "h-[100px] w-full" : "h-[180px] w-full"} />
      ) : !hasData ? (
        <Box position="relative">
          {chartWidth > 0 && (() => {
            const available = chartWidth - yAxisWidth - initialSpacing - endSpacing
            const spacing = Math.max(8, Math.floor(available / 23))
            const ghostKnockData = Array.from({ length: 24 }, (_, h) => ({
              value: at(GHOST_KNOCK_VALUES, h),
              label: h % 3 === 0 ? formatHour(h) : '',
              labelTextStyle: ghostLabelStyle,
            }))
            const ghostContactsData = Array.from({ length: 24 }, (_, h) => ({
              value: at(GHOST_CONTACTS_VALUES, h),
            }))
            return (
              <Box opacity={10}>
                <GoldenHourLineChart
                  data={ghostKnockData}
                  data2={ghostContactsData}
                  curvature={0.2}
                  color1={"var(--light-purple)"}
                  color2={"var(--teal)"}
                  thickness1={2}
                  thickness2={2.5}
                  startFillColor1={`color-mix(in oklab, var(--light-purple) 25%, transparent)`}
                  endFillColor1={`color-mix(in oklab, var(--light-purple) 2%, transparent)`}
                  startFillColor2={`color-mix(in oklab, var(--teal) 30%, transparent)`}
                  endFillColor2={`color-mix(in oklab, var(--teal) 2%, transparent)`}
                  noOfSections={compact ? 3 : 4}
                  yAxisTextStyle={ghostYAxisStyle}
                  height={chartHeight}
                  rulesColor={borderColor}
                  dashWidth={4}
                  dashGap={4}
                  spacing={spacing}
                  initialSpacing={initialSpacing}
                  endSpacing={endSpacing}
                  xAxisLabelsVerticalShift={4}
                  labelsExtraHeight={20}
                  yAxisLabelWidth={yAxisWidth}
                />
              </Box>
            )
          })()}
          {chartWidth > 0 && (() => {
            const available = chartWidth - yAxisWidth - initialSpacing - endSpacing
            const spacing = Math.max(8, Math.floor(available / 23))
            const startX = yAxisWidth + initialSpacing + 15 * spacing - spacing * 0.5
            const endX = yAxisWidth + initialSpacing + 18 * spacing + spacing * 0.5
            return (
              <Box
                opacity={10} borderStyle="dashed" pointerEvents="none" position="absolute"
                left={startX}
                top={0}
                width={Math.max(0, endX - startX)}
                height={chartHeight}
                background="warning"
                rounded="sm"
                border={['left', 'right']}
                borderColor="warning"
              />
            )
          })()}
          <Box position="absolute" left={0} right={0} top={0} bottom={0} align="center" justify="center">
            <Text size="sm" weight="medium">No activity data</Text>
          </Box>
        </Box>
      ) : null}
      {hasData && chartWidth > 0 && (() => {
        const available = chartWidth - yAxisWidth - initialSpacing - endSpacing
        const spacing = Math.max(8, Math.floor(available / 23))

        const allKnocksData = Array.from({ length: 24 }, (_, h) => ({
          value: at(allCounts, h),
          label: h % 3 === 0 ? formatHour(h) : '',
          labelTextStyle: { color: mutedColor, fontSize: 9, textAlign: 'center' as const },
        }))

        const contactsData = Array.from({ length: 24 }, (_, h) => ({
          value: at(contactCounts, h),
        }))

        const hourZones = onHourPress ? Array.from({ length: 24 }, (_, h) => {
          const x = yAxisWidth + initialSpacing + h * spacing - spacing * 0.5
          const isHovered = hoveredHour === h
          return (
            <Box
              key={h}
              position="absolute"
              left={x}
              top={0}
              width={spacing}
              height={chartHeight}
              cursor="pointer"
              zIndex={2}
              rounded="sm"
              hoverBackground
              onPointerEnter={() => setHoveredHour(h)}
              onPointerLeave={() =>
                setHoveredHour((current) => (current === h ? null : current))
              }
              onClick={() => onHourPress(h, formatHour(h))}
            >
              {isHovered && (
                <Box
                  pointerEvents="none" position="absolute"
                  left={spacing / 2}
                  top={-12}
                  paddingX="sm"
                  paddingY="xs"
                  rounded="sm"
                  surface="popover"
                  border
                  borderColor="border"
                  className="-translate-x-1/2"
                  shrink={false}
                  zIndex={3}
                >
                  <Text size="2xs" weight="medium" truncate={1}>
                    {formatHour(h)}
                  </Text>
                  <Text size="2xs" weight="medium" truncate={1}>
                    KNC: {allCounts[h]}
                  </Text>
                  <Text size="2xs" weight="medium" truncate={1}>
                    Golden Hour: {contactCounts[h]}
                  </Text>
                </Box>
              )}
            </Box>
          )
        }) : null

        const goldenOverlay = goldenRange ? (() => {
          const startX = yAxisWidth + initialSpacing + goldenRange.start * spacing - spacing * 0.5
          const endX = yAxisWidth + initialSpacing + goldenRange.end * spacing + spacing * 0.5
          return (
            <Box
              opacity={10} borderStyle="dashed" pointerEvents="none" position="absolute"
              left={startX}
              top={0}
              width={Math.max(0, endX - startX)}
              height={chartHeight}
              background="warning"
              rounded="sm"
              border={['left', 'right']}
              borderColor="warning"
              zIndex={0}
            />
          )
        })() : null

        const chart = (
          <GoldenHourLineChart
            data={allKnocksData}
            data2={contactsData}
            curvature={0.2}
            color1={"var(--light-purple)"}
            color2={"var(--teal)"}
            thickness1={2}
            thickness2={2.5}
            startFillColor1={`color-mix(in oklab, var(--light-purple) 25%, transparent)`}
            endFillColor1={`color-mix(in oklab, var(--light-purple) 2%, transparent)`}
            startFillColor2={`color-mix(in oklab, var(--teal) 30%, transparent)`}
            endFillColor2={`color-mix(in oklab, var(--teal) 2%, transparent)`}
            noOfSections={compact ? 3 : 4}
            yAxisTextStyle={{ color: mutedColor, fontSize: compact ? 8 : 10 }}
            height={chartHeight}
            rulesColor={borderColor}
            dashWidth={4}
            dashGap={4}
            spacing={spacing}
            initialSpacing={initialSpacing}
            endSpacing={endSpacing}
            yAxisLabelWidth={yAxisWidth}
            xAxisLabelsVerticalShift={4}
            labelsExtraHeight={20}
          />
        )

        return (
          <Box ref={chartWrapRef} position="relative">
            {goldenOverlay}
            {chart}
            {hourZones}
          </Box>
        )
      })()}
    </Box>
  )

  if (compact) {
    return (
      <>
        {legend}
        {chartBox}
      </>
    )
  }

  return (
    <Section
      title="Golden Hour"
      subtitle={subtitle}
      titleExtra={
        <InfoTooltip>
          Golden Hour is the best time to {labels?.contactVerb}, calculated from your age update reasons — using visits where you spoke to someone vs. ones where nobody answered.
        </InfoTooltip>
      }
    >
      {legend}
      {chartBox}

      {goldenRange && goldenStats && hasData && (
        <Box paddingTop="sm" rounded="md" surface="sunken" padding="sm" border>
          <Box direction="row" gap="md" justify="around">
            <Box align="center">
              <Text size="xs" weight="semibold" color="yellow">
                {formatHourRange(goldenRange.start, goldenRange.end)}
              </Text>
              <Text size="2xl" weight="bold">{Math.round(goldenStats.goldenRate)}%</Text>
              <Text size="2xs" variant="muted">contact rate</Text>
              <Text size="2xs" variant="muted">
                {goldenStats.goldenContacts} contacts / {goldenStats.goldenKnocks} {labels?.contactVerb}s
              </Text>
            </Box>
            <Box align="center">
              <Text size="xs" variant="muted" weight="semibold">Other hours</Text>
              <Text size="2xl" weight="bold" variant="muted">{Math.round(goldenStats.otherRate)}%</Text>
              <Text size="2xs" variant="muted">contact rate</Text>
              <Text size="2xs" variant="muted">
                {goldenStats.otherContacts} contacts / {goldenStats.otherKnocks} {labels?.contactVerb}s
              </Text>
            </Box>
          </Box>
        </Box>
      )}
    </Section>
  )
}
